import { ChecklistItem } from '../types/api';

export interface TagGroupNode {
  id: string;
  canonicalPath: string;
  displayPath: string;
  label: string;
  firstIndex: number;
  depth: number;
  itemIds: string[];
  children: TagGroupNode[];
}

export interface TagItemNode {
  id: string;
  item: ChecklistItem;
  firstIndex: number;
}

export interface GroupedTagModel {
  topLevel: Array<TagGroupNode | TagItemNode>;
  groupsById: Map<string, TagGroupNode>;
  visualCount: number;
}

export interface FlatGroupedEntry {
  kind: 'group' | 'item';
  depth: number;
  groupedContext: boolean;
  group?: TagGroupNode;
  item?: ChecklistItem;
}

function canonicalizePath(path: string): string {
  return path
    .split(':')
    .map(segment => segment.trim())
    .filter(Boolean)
    .join(':')
    .toLowerCase();
}

function cleanDisplayPath(path: string): string {
  return path
    .split(':')
    .map(segment => segment.trim())
    .filter(Boolean)
    .join(':');
}

function pathDepth(path: string): number {
  if (!path) return 0;
  return path.split(':').length;
}

function parentCanonicalPath(path: string): string {
  const parts = path.split(':');
  if (parts.length <= 1) return '';
  return parts.slice(0, parts.length - 1).join(':');
}

function splitPrefixes(displayPath: string): string[] {
  const segments = displayPath.split(':').map(s => s.trim()).filter(Boolean);
  const prefixes: string[] = [];
  for (let i = 1; i <= segments.length; i += 1) {
    prefixes.push(segments.slice(0, i).join(':'));
  }
  return prefixes;
}

function readCategoryPaths(item: ChecklistItem): string[] {
  const categories = item.tags?.categories || [];
  const seen = new Set<string>();
  const paths: string[] = [];

  categories.forEach(raw => {
    const display = cleanDisplayPath(raw);
    if (!display) return;
    const key = canonicalizePath(display);
    if (seen.has(key)) return;
    seen.add(key);
    paths.push(display);
  });

  return paths;
}

export function buildGroupedTagModel(items: ChecklistItem[]): GroupedTagModel {
  const prefixItemSets = new Map<string, Set<string>>();
  const prefixDisplay = new Map<string, string>();
  const prefixFirstIndex = new Map<string, number>();
  const itemPathsByID = new Map<string, string[]>();

  items.forEach((item, index) => {
    const paths = readCategoryPaths(item);
    itemPathsByID.set(item.id, paths);

    paths.forEach(displayPath => {
      splitPrefixes(displayPath).forEach(displayPrefix => {
        const canonicalPrefix = canonicalizePath(displayPrefix);
        if (!canonicalPrefix) return;

        if (!prefixItemSets.has(canonicalPrefix)) {
          prefixItemSets.set(canonicalPrefix, new Set<string>());
        }
        prefixItemSets.get(canonicalPrefix)!.add(item.id);

        if (!prefixDisplay.has(canonicalPrefix)) {
          prefixDisplay.set(canonicalPrefix, displayPrefix);
        }

        const prevIndex = prefixFirstIndex.get(canonicalPrefix);
        if (prevIndex === undefined || index < prevIndex) {
          prefixFirstIndex.set(canonicalPrefix, index);
        }
      });
    });
  });

  const groupable = new Set<string>();
  prefixItemSets.forEach((itemSet, prefix) => {
    if (itemSet.size >= 2) {
      groupable.add(prefix);
    }
  });

  const groupsById = new Map<string, TagGroupNode>();

  const ensureGroupNode = (prefix: string): TagGroupNode => {
    const existing = groupsById.get(prefix);
    if (existing) {
      return existing;
    }

    const displayPath = prefixDisplay.get(prefix) || prefix;
    const segments = displayPath.split(':');
    const created: TagGroupNode = {
      id: prefix,
      canonicalPath: prefix,
      displayPath,
      label: segments[segments.length - 1],
      firstIndex: prefixFirstIndex.get(prefix) ?? Number.MAX_SAFE_INTEGER,
      depth: pathDepth(prefix),
      itemIds: [],
      children: [],
    };
    groupsById.set(prefix, created);
    return created;
  };

  groupable.forEach(prefix => {
    ensureGroupNode(prefix);
  });

  const ungroupedItems: TagItemNode[] = [];

  items.forEach((item, index) => {
    const itemPaths = itemPathsByID.get(item.id) || [];
    let selectedPathPrefixes: string[] = [];
    let selectedDeepestGroupableIndex = -1;

    itemPaths.forEach(displayPath => {
      const canonicalPrefixes = splitPrefixes(displayPath).map(prefix => canonicalizePath(prefix)).filter(Boolean);
      let deepestGroupableIndex = -1;

      canonicalPrefixes.forEach((prefix, prefixIndex) => {
        if (groupable.has(prefix)) {
          deepestGroupableIndex = prefixIndex;
        }
      });

      if (deepestGroupableIndex === -1) {
        return;
      }

      if (deepestGroupableIndex > selectedDeepestGroupableIndex) {
        selectedDeepestGroupableIndex = deepestGroupableIndex;
        selectedPathPrefixes = canonicalPrefixes;
        return;
      }

      if (deepestGroupableIndex === selectedDeepestGroupableIndex) {
        const currentRoot = canonicalPrefixes[deepestGroupableIndex] || '';
        const selectedRoot = selectedPathPrefixes[selectedDeepestGroupableIndex] || '';
        const currentFirstIndex = prefixFirstIndex.get(currentRoot) ?? Number.MAX_SAFE_INTEGER;
        const selectedFirstIndex = prefixFirstIndex.get(selectedRoot) ?? Number.MAX_SAFE_INTEGER;

        if (currentFirstIndex < selectedFirstIndex) {
          selectedPathPrefixes = canonicalPrefixes;
        }
      }
    });

    if (selectedDeepestGroupableIndex === -1 || selectedPathPrefixes.length === 0) {
      ungroupedItems.push({ id: item.id, item, firstIndex: index });
      return;
    }

    for (let prefixIndex = selectedDeepestGroupableIndex + 1; prefixIndex < selectedPathPrefixes.length; prefixIndex += 1) {
      ensureGroupNode(selectedPathPrefixes[prefixIndex]);
    }

    const attachGroupCanonical = selectedPathPrefixes[selectedPathPrefixes.length - 1];
    const targetGroup = ensureGroupNode(attachGroupCanonical);
    targetGroup.itemIds.push(item.id);
  });

  const itemByID = new Map(items.map(item => [item.id, item]));
  const itemIndexByID = new Map(items.map((item, index) => [item.id, index]));

  const rootGroups: TagGroupNode[] = [];
  groupsById.forEach(group => {
    group.children = [];
  });

  groupsById.forEach(group => {
    let parent = parentCanonicalPath(group.id);
    let parentGroup: TagGroupNode | undefined;
    while (parent) {
      const candidate = groupsById.get(parent);
      if (candidate) {
        parentGroup = candidate;
        break;
      }
      parent = parentCanonicalPath(parent);
    }

    if (parentGroup) {
      parentGroup.children.push(group);
    } else {
      rootGroups.push(group);
    }
  });

  const sortGroupTree = (group: TagGroupNode) => {
    group.children.sort((a, b) => a.firstIndex - b.firstIndex);
    group.children.forEach(sortGroupTree);
    group.itemIds.sort((a, b) => {
      const aIdx = itemIndexByID.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bIdx = itemIndexByID.get(b) ?? Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    });
  };

  rootGroups.sort((a, b) => a.firstIndex - b.firstIndex);
  rootGroups.forEach(sortGroupTree);

  const topLevel: Array<TagGroupNode | TagItemNode> = [
    ...rootGroups,
    ...ungroupedItems,
  ].sort((a, b) => a.firstIndex - b.firstIndex);

  // Drop empty groups if all descendants ended up empty (defensive).
  const hasVisibleChildren = (group: TagGroupNode): boolean => {
    const hasItems = group.itemIds.some(id => itemByID.has(id));
    const hasGroupChildren = group.children.some(child => hasVisibleChildren(child));
    return hasItems || hasGroupChildren;
  };

  const filteredTopLevel = topLevel.filter(entry => {
    if ('item' in entry) return true;
    return hasVisibleChildren(entry);
  });

  return {
    topLevel: filteredTopLevel,
    groupsById,
    visualCount: filteredTopLevel.length,
  };
}

export function flattenGroupedTagModel(
  model: GroupedTagModel,
  items: ChecklistItem[],
  expandedGroupIds: Set<string>
): FlatGroupedEntry[] {
  const itemByID = new Map(items.map(item => [item.id, item]));
  const rows: FlatGroupedEntry[] = [];

  const visitGroup = (group: TagGroupNode, depth: number) => {
    rows.push({ kind: 'group', depth, groupedContext: false, group });

    if (!expandedGroupIds.has(group.id)) {
      return;
    }

    const children: Array<{ firstIndex: number; type: 'group' | 'item'; group?: TagGroupNode; item?: ChecklistItem }> = [];

    group.children.forEach(child => {
      children.push({ firstIndex: child.firstIndex, type: 'group', group: child });
    });

    group.itemIds.forEach(itemID => {
      const item = itemByID.get(itemID);
      if (item) {
        const idx = items.findIndex(it => it.id === itemID);
        children.push({ firstIndex: idx, type: 'item', item });
      }
    });

    children.sort((a, b) => a.firstIndex - b.firstIndex);

    children.forEach(child => {
      if (child.type === 'group' && child.group) {
        visitGroup(child.group, depth + 1);
      }
      if (child.type === 'item' && child.item) {
        rows.push({ kind: 'item', depth: depth + 1, groupedContext: true, item: child.item });
      }
    });
  };

  model.topLevel.forEach(entry => {
    if ('item' in entry) {
      rows.push({ kind: 'item', depth: 0, groupedContext: false, item: entry.item });
      return;
    }

    visitGroup(entry, 0);
  });

  return rows;
}
